import { useEffect, useRef, useState } from "react"
import { Input } from "./Input"
import { DateRange, DayPicker } from "react-day-picker";
import { et } from "react-day-picker/locale";
import { format, isValid, parse } from "date-fns";
import useClickOutsideRef from "../hooks/useClickOutsideRef";
import { useAppSelector } from "../../store/hooks";
import { DisplayType } from "../../store/features/displaySlice";
import ReactDOM from "react-dom";

export interface DatePickerComponentProps {
    initialValue?: Date;
    dateValues?: DateRange;
    dateChanged?: (val: Date | undefined) => void;
    datesChanged?: (val: DateRange | undefined) => void;
    placeholder?: string;
    isInvalid?: boolean;
    type?: "startDate" | "endDate";
    mode: "single" | "range";
    disablePastDates?: boolean;
}

export const DatePickerComponent: React.FC<DatePickerComponentProps> = ({disablePastDates = true, ...props}) => {

    const dateModalRef = useRef<any>(null);
    const dateOpenRef = useRef<any>();
    const dateInputRef = useRef<any>();
    const mobileDatePickerRef = useRef<any>();
    const isMobileDisplay = useAppSelector((state) => state.display.type);
    const [popupPosition, setPopupPosition] = useState<string>("bottom");

    // Hold the month in state to control the calendar when the input changes
    const [month, setMonth] = useState(props.initialValue ? props.initialValue : (props.dateValues?.from && props.type === "startDate") ? props.dateValues.from :
        (props.dateValues?.to && props.type === "endDate") ? props.dateValues.to : new Date());

    // Hold the selected date in state
    const [selectedDate, setSelectedDate] = useState<Date | undefined>(props.initialValue);
    const [selectedDates, setSelectedDates] = useState<DateRange | undefined>(props.dateValues);

    const displayType = useAppSelector(state => state.display.type)

    useEffect(() => {
        if (props.dateChanged) {
            props.dateChanged(selectedDate);
        }
    }, [selectedDate])

    useEffect(() => {
        if (props.datesChanged) {
            props.datesChanged(selectedDates);
        }
    }, [selectedDates])

    useEffect(() => {
        if (props.dateValues?.from != selectedDates?.from || props.dateValues?.to != selectedDates?.to) {
            handleDateRangePickerChangeFromProp(props.dateValues);
        }
    }, [props.dateValues])

    // Hold the input value in state
    const [inputValue, setInputValue] = useState(props.initialValue ? format(props.initialValue, "MM/dd/yyyy") : (props.dateValues?.from && props.type === "startDate") ?
        format(props.dateValues.from, "MM/dd/yyyy") : (props.dateValues?.to && props.type === "endDate") ? format(props.dateValues.to, "MM/dd/yyyy") : "")

    const [modalOpenState, setModalOpenState] = useState(false);

    const open = useClickOutsideRef(dateModalRef, modalOpenState, [], displayType === DisplayType.MOBILE);

    useEffect(() => {
        if (dateOpenRef.current) {
            if (dateOpenRef.current.getBoundingClientRect().right > window.innerWidth && dateOpenRef.current?.getBoundingClientRect().right - window.innerWidth) {
                dateOpenRef.current.style.left = "-" + (dateOpenRef.current.getBoundingClientRect().right - document.body.clientWidth).toString() + "px";
            }
        }
        setModalOpenState(open);
    }, [open, dateOpenRef])

    useEffect(() => {
        if (open && displayType === DisplayType.MOBILE) {
            document.body.append(mobileDatePickerRef.current);
        }
        return () => {
            // Clean up: remove the element from the body when closed
            if (mobileDatePickerRef?.current) {
                document.body.removeChild(mobileDatePickerRef.current);
            }
        };
    }, [open])

    const handleDayPickerSelect = (date: Date | undefined) => {
        setModalOpenState(false);
        if (!date) {
            setInputValue("");
            setSelectedDate(undefined);
        } else {
            setSelectedDate(date);
            setMonth(date);
            setInputValue(format(date, "MM/dd/yyyy"));
        }
    };

    const handleDateRangePickerChangeFromProp = (dates: DateRange | undefined) => {
        setSelectedDates(dates);
        if (dates?.from && !selectedDates?.to && props.type === "endDate") {
            setMonth(dates.from);
        }
        if (dates?.to && !selectedDates?.from && props.type === "startDate") {
            setMonth(dates.to);
        }
        if (props.type === "startDate") {
            setInputValue(dates?.from ? format(dates.from, "MM/dd/yyyy") : "");
        } else {
            setInputValue(dates?.to ? format(dates.to, "MM/dd/yyyy") : "");
        }
    }

    const handleDayPickerSelectRange = (date: DateRange | undefined) => {
        setModalOpenState(false);
        // edge case for allowing to set same enddate as startdate for range
        if (!date && selectedDates?.from && props.type === "endDate") {
            let toDate = selectedDates?.from;
            setInputValue(toDate ? format(toDate, "MM/dd/yyyy") : "");
            setMonth(toDate);
            setSelectedDates({from: selectedDates.from, to: toDate});
            return;
        }
        if (!date) {
            setInputValue("");
            setSelectedDates(undefined);
        } else {
            let selDate = Object.create(selectedDates ? selectedDates : null);
            if (selDate === undefined) {
                selDate = {
                    from: undefined,
                    to: undefined
                }
            } else if (!selDate.to) {
                selDate = {
                    from: selDate.from,
                    to: undefined
                }
            }
            if (props.type === "startDate") {
                selDate.from = date.from;
                if (date.to && date.to != selectedDates?.to) {
                    selDate.from = date.to;
                }
                if ((selDate.from && selectedDates?.from) ? format(selDate.from, 'MM/dd/yyyy') == format(selectedDates?.from!, 'MM/dd/yyyy') : false) {
                    selDate.from = undefined;
                }
                setInputValue(selDate?.from ? format(selDate.from, "MM/dd/yyyy") : "");
                if (selDate.from) {
                    setMonth(selDate.from);
                }
            } else {
                selDate.to = date.to;
                if (date.from && date.from != selectedDates?.from) {
                    selDate.to = date.from;
                }
                if ((selDate.to && selectedDates?.to) ? format(selDate.to, 'MM/dd/yyyy') == format(selectedDates?.to!, 'MM/dd/yyyy') : false) {
                    selDate.to = undefined;
                }
                setInputValue(selDate?.to ? format(selDate.to, "MM/dd/yyyy") : "");
                if (selDate.to) {
                    setMonth(selDate.to);
                }
            }
            setSelectedDates(selDate);
        }
    };

    const handleInputChange = (e: string) => {
        setInputValue(e); // keep the input value in sync

        const parsedDate = parse(e, "MM/dd/yyyy", new Date());

        if (props.mode == "single") {
            if (isValid(parsedDate)) {
                setSelectedDate(parsedDate);
                setMonth(parsedDate);
            } else {
                setSelectedDate(undefined);
            }
        } else {
            let selDate = Object.create(selectedDates ? selectedDates : null);
            if (selDate === undefined) {
                selDate = {
                    from: undefined,
                    to: undefined
                }
            } else if (!selDate.to) {
                selDate = {
                    from: selDate.from,
                    to: undefined
                }
            }
            if (props.type === "startDate") {
                if (isValid(parsedDate)) {
                    selDate.from = parsedDate;
                    setMonth(parsedDate);
                } else {
                    selDate.from = undefined;
                }
            } else {
                if (isValid(parsedDate)) {
                    selDate.to = parsedDate;
                    setMonth(parsedDate);
                } else {
                    selDate.to = undefined;
                }
            }
            setSelectedDates(selDate);
        }
    };

    const getIfParentElm = (elm: any) => {
        const isModalDatePicker = elm.current?.parentNode == document.body
        return isModalDatePicker ? "flex" : "none"
    }

    const calculatePopupModalPosition = () => {
        if (open && dateInputRef.current && dateOpenRef.current) {
            const rect = dateInputRef.current.getBoundingClientRect(); // Get position of the input element
            const windowHeight = window.innerHeight;
            const popupHeight = dateOpenRef.current.clientHeight; // Set a fixed height for your popup or calculate dynamically
            const spaceBelow = windowHeight - rect.bottom; // Space from the bottom of the screen to the input
            const spaceAbove = rect.top; // Space from the top of the screen to the input

            // Determine whether to show the popup above or below
            if (spaceBelow >= popupHeight) {
                // If there's enough space below, show the popup at the bottom
                setPopupPosition("bottom");
            } else if (spaceAbove >= popupHeight) {
                // If there's enough space above, show the popup at the top
                setPopupPosition("top");
            } else {
                // Default case: Show the popup at the bottom if no space available above
                setPopupPosition("bottom");
            }

        }
    }
    useEffect(() => {
        calculatePopupModalPosition();
        window.addEventListener("scroll", calculatePopupModalPosition);
    }, [open]);

    return (
        <div>
            <div className="z-20 relative" ref={dateModalRef}>
                <div ref={dateInputRef}>
                    <Input value={inputValue} placeHolder={props.placeholder ? props.placeholder : "Kuupäev"} onChange={(e) => handleInputChange(e)} blueShadow={true} invalid={props.isInvalid} />
                </div>
                {open ?
                    <>
                        {
                            isMobileDisplay === DisplayType.MOBILE ?
                                ReactDOM.createPortal(
                                    <div className="fixed w-full h-full bg-red top-0 left-0 hidden items-center justify-center align-middle z-[200] bg-slate-500/50" ref={mobileDatePickerRef} style={{ display: getIfParentElm(mobileDatePickerRef) }}
                                        onClick={(e) => {
                                            setModalOpenState(false);
                                        }}>
                                        <div className="w-fit flex justify-center" onClick={(e) => e.stopPropagation()}>
                                            {
                                                props.mode == "single" ?
                                                    <DayPicker className="bg-white p-2 rounded-lg" mode={"single"} selected={selectedDate} onSelect={(v) => handleDayPickerSelect(v)} locale={et} month={month} onMonthChange={setMonth} disabled={{ before: disablePastDates ? new Date() : new Date(1900, 1, 1) }}
/>
                                                    :
                                                    <DayPicker className="bg-white p-2 rounded-lg" mode={"range"} selected={selectedDates} onSelect={(v) => handleDayPickerSelectRange(v)} locale={et} month={month} onMonthChange={setMonth} disabled={{ before: disablePastDates ? new Date() : new Date(1900, 1, 1) }}
/>
                                            }
                                        </div>
                                    </div>,
                                    document.body
                                )
                                :
                                <div className={`${popupPosition == "top" ? "bottom-full" : "top-full"} absolute bg-white shadow-main p-2 rounded-xl`} ref={dateOpenRef}>
                                    {
                                        props.mode == "single" ?
                                            <DayPicker mode={"single"} selected={selectedDate} onSelect={(v) => handleDayPickerSelect(v)} locale={et} month={month} onMonthChange={setMonth} disabled={{ before: disablePastDates ? new Date() : new Date(1900, 1, 1) }}/>
                                            :
                                            <DayPicker mode={"range"} selected={selectedDates} onSelect={(v) => handleDayPickerSelectRange(v)} locale={et} month={month} onMonthChange={setMonth} disabled={{ before: disablePastDates ? new Date() : new Date(1900, 1, 1) }}
/>
                                    }
                                </div>
                        }
                    </>
                    :
                    <></>}
            </div>
        </div>
    )
}
